import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { showLoading, hideLoading } from "../../../redux/actions/loadingActions";
import { mostrarAviso, mostrarError, mostrarInfo } from "../../utils/Alertas";
import { TextField } from "@mui/material";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { getFunctions, httpsCallable } from "firebase/functions";
import { app } from "../../firebase";

interface Props {
	onSuccess: () => void;
}

const RegisterForm: React.FC<Props> = ({ onSuccess }) => {
	const dispatch = useDispatch();
	const { executeRecaptcha } = useGoogleReCaptcha();

	const [nombre, setNombre] = useState("");
	const [email, setEmail] = useState("");
	const [otp, setOtp] = useState("");
	const [password, setPassword] = useState("");
	const [confirm, setConfirm] = useState("");
	const [otpSent, setOtpSent] = useState(false);
    const [canResendOtp, setCanResendOtp] = useState(true);

	const functions = getFunctions(app, "us-central1");
	const sendOtpFn = httpsCallable(functions, "sendRegisterOtp");
	const registerFn = httpsCallable(functions, "registerWithOtp");

	// -----------------------------
	// 📩 Enviar código OTP
	// -----------------------------
	const handleSendOtp = async () => {
		if (!email.trim() || !nombre.trim()) {
			mostrarInfo("Introduce nombre y correo electrónico.");
			return;
		}

		const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		if (!emailRegex.test(email)) {
			mostrarError("El correo no tiene un formato válido.");
			return;
		}

		if (!executeRecaptcha) {
			mostrarError("No se pudo validar el formulario.");
			return;
		}

		dispatch(showLoading());
		try {
			const recaptchaToken = await executeRecaptcha("register");

			await sendOtpFn({
				nombre,
				email,
				recaptchaToken,
			});

            setOtpSent(true);
            setCanResendOtp(false);

            setTimeout(() => {
                setCanResendOtp(true);
            }, 5000);

			mostrarAviso("Te hemos enviado un código a tu correo.");
		} catch (err: any) {
			const code = err?.code;

			switch (code) {
				case "functions/already-exists":
					mostrarError("El correo ya está registrado.");
					break;
                case "functions/failed-precondition":
					mostrarError("No se puede registrar este correo. La solicitud fue rechazada.");
					break;
				case "functions/permission-denied":
					mostrarError("No se pudo validar la seguridad.");
					break;
				default:
					console.error(err);
					mostrarError("No se pudo enviar el código.");
			}
		} finally {
			dispatch(hideLoading());
		}
	};

	// -----------------------------
	// 🧾 Registro final
	// -----------------------------
	const handleRegister = async (e: React.FormEvent) => {
		e.preventDefault();

		if (!nombre || !email || !otp || !password || !confirm) {
			mostrarInfo("Completa todos los campos.");
			return;
		}

		if (password.length < 6) {
			mostrarError("La contraseña debe tener al menos 6 caracteres.");
			return;
		}

		if (password !== confirm) {
			mostrarError("Las contraseñas no coinciden.");
			return;
		}

		dispatch(showLoading());
		try {
			await registerFn({
				nombre,
				email,
				password,
				otp,
			});

			mostrarAviso(
				"Registro completado. Tu cuenta quedará activa tras la aprobación de un administrador."
			);

			onSuccess();
		} catch (err: any) {
			const code = err?.code;

			switch (code) {
				case "functions/invalid-argument":
					mostrarError("Código incorrecto.");
					break;
				case "functions/deadline-exceeded":
					mostrarError("El código ha expirado.");
					break;
				case "functions/permission-denied":
					mostrarError("Demasiados intentos fallidos.");
					break;
				case "functions/already-exists":
					mostrarError("El correo ya está registrado.");
					break;
				default:
					console.error(err);
					mostrarError("No se pudo completar el registro.");
			}
		} finally {
			dispatch(hideLoading());
		}
	};

	return (
		<form onSubmit={handleRegister} className="auth-form">
			<h2>Registro</h2>

			<TextField
				label="Nombre"
				value={nombre}
				onChange={(e) => setNombre(e.target.value)}
				fullWidth
				margin="normal"
			/>

			<TextField
				label="Correo electrónico"
				type="email"
				value={email}
				onChange={(e) => setEmail(e.target.value)}
				fullWidth
				margin="normal"
			/>

			<button
                type="button"
                onClick={handleSendOtp}
                disabled={!canResendOtp}
                className="text-action-btn"
            >
                {canResendOtp ? "Enviar código" : "Código enviado. Revisa tu correo."}
            </button>

			<TextField
				label="Código recibido"
				value={otp}
				onChange={(e) => setOtp(e.target.value)}
				fullWidth
				margin="normal"
				disabled={!otpSent}
			/>

			<TextField
				label="Contraseña"
				type="password"
				value={password}
				onChange={(e) => setPassword(e.target.value)}
				fullWidth
				margin="normal"
				disabled={!otp}
			/>

			<TextField
				label="Confirmar contraseña"
				type="password"
				value={confirm}
				onChange={(e) => setConfirm(e.target.value)}
				fullWidth
				margin="normal"
				disabled={!otp}
			/>

			<button type="submit">Registrarse</button>

			<p className="recaptcha-info mb-0">
				🔒 Protegido por reCAPTCHA ·{" "}
				<a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer">
					Privacidad
				</a>{" "}
				·{" "}
				<a href="https://policies.google.com/terms" target="_blank" rel="noreferrer">
					Términos
				</a>
			</p>
		</form>
	);
};

export default RegisterForm;
